import { getFile, getJSONRequest, getRequest, getUpload } from "../functions";


//** ----- МЕТОДЫ РАБОТЫ С ПОЛЬЗОВАТЕЛЯМИ ----- */
export const UserApi = {
	/**
	 * API метод «Users», отдающий список пользователей с учетом фильтра страницы
	 * Без параметров запрашивает активных пользователей, не отфильтрованных как получатели чаевых.
	 * @param {{ Active : boolean, TipsRecipient : boolean }} params параметры фильтра:
	 * Active — активные или удаленные пользователи;
	 * TipsRecipient — только получатели чаевых или все пользователи
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Users : [object] }} data ответ от сервера
	 */
	getList(params = { Active : true, TipsRecipient : false }, callback) {
		getRequest("Users", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «User», отдающий пользователя по ID
	 * @param {number} id ID пользователя
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	getById(id, callback) {
		getRequest("User", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «UserSave», сохраняющий пользователя
	 * @param {object} params параметры формы пользователя
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
	 */
	save(params, callback) {
		getRequest("UserSave", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «UserDelete», удаляющий или восстанавливающий пользователя по ID
	 * Фактическое действие определяется текущим состоянием пользователя на сервере.
	 * @param {number} id ID пользователя
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	remove(id, callback) {
		getRequest("UserDelete", { Id : id }, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С КУРЬЕРАМИ ----- */
export const CourierApi = {
	/**
	 * API метод «Couriers», отдающий список курьеров с учетом фильтра страницы
	 * @param {{ Active : boolean }} params параметры фильтра активных или удаленных курьеров
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Users : [object] }} data ответ от сервера
	 */
	getList(params = { Active : true }, callback) {
		getRequest("Couriers", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «ToggleCourierShift», открывающий или закрывающий смену курьера
	 * Направление переключения определяется параметром Active в собранных данных смены.
	 * @param {object} params параметры смены курьера
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	toggleShift(params, callback) {
		getRequest("ToggleCourierShift", params, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С ПАРОЛЯМИ ----- */
export const PasswordApi = {
	/**
	 * API метод «GenerateUserPassword», сбрасывающий пароль пользователя
	 * Новый пароль отправляется пользователю на указанный в его карточке email.
	 * @param {number} userId ID пользователя
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	reset(userId, callback) {
		getRequest("GenerateUserPassword", { UserId : userId }, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С ПРЕДПРИЯТИЯМИ ----- */
export const MerchantApi = {
	/**
	 * API метод «GroupMerchants», отдающий список доступных пользователю предприятий
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Merchants : [object] }} data ответ от сервера
	 */
	getList(callback) {
		getRequest("GroupMerchants", {}, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С ДОЛЖНОСТЯМИ ----- */
export const RoleApi = {
	/**
	 * API метод «Roles», отдающий список должностей текущего или всех доступных предприятий
	 * @param {boolean} [allMerchants=false] флаг получения должностей всех доступных предприятий
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Roles : [object] }} data ответ от сервера
	 */
	getList(allMerchants = false, callback) {
		getRequest("Roles", { AllMerchants : allMerchants }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Role», отдающий должность по ID
	 * @param {number} id ID должности
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	getById(id, callback) {
		getRequest("Role", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «RoleSave», сохраняющий должность
	 * @param {object} params параметры формы должности
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
	 */
	save(params, callback) {
		getRequest("RoleSave", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «RoleRemove», удаляющий должность по ID
	 * @param {number} id ID удаляемой должности
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	remove(id, callback) {
		getRequest("RoleRemove", { Id : id }, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С ОТДЕЛАМИ ----- */
export const DepartmentApi = {
	/**
	 * API метод «Departments», отдающий список отделов текущего предприятия
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Departments : [object] }} data ответ от сервера
	 */
	getList(callback) {
		getRequest("Departments", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «DepartmentSave», сохраняющий отдел
	 * @param {object} params параметры формы отдела
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
	 */
	save(params, callback) {
		getRequest("DepartmentSave", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «DepartmentRemove», удаляющий отдел по ID
	 * @param {number} id ID удаляемого отдела
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	remove(id, callback) {
		getRequest("DepartmentRemove", { Id : id }, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ ПОЛУЧЕНИЯ СИСТЕМНЫХ СПРАВОЧНИКОВ ----- */
export const SystemApi = {
	/**
	 * API метод «Rights», отдающий список доступных прав
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Rights : [object] }} data ответ от сервера
	 */
	getRights(callback) {
		getRequest("Rights", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Dashboards», отдающий список доступных рабочих столов
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Dashboards : [object] }} data ответ от сервера
	 */
	getDashboards(callback) {
		getRequest("Dashboards", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «NotificationTypes», отдающий список доступных типов уведомлений
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Notifications : [object] }} data ответ от сервера
	 */
	getNotificationTypes(callback) {
		getRequest("NotificationTypes", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Cities», отдающий список городов
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Cities : [object] }} data ответ от сервера
	 */
	getCities(callback) {
		getRequest("Cities", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Brands», отдающий список брендов текущего предприятия
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Brands : [object] }} data ответ от сервера
	 */
	getBrands(callback) {
		getRequest("Brands", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Entities», отдающий список юридических лиц текущего предприятия
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Entities : [object] }} data ответ от сервера
	 */
	getEntities(callback) {
		getRequest("Entities", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Aggregators», отдающий список агрегаторов текущего предприятия
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Aggregators : [object] }} data ответ от сервера
	 */
	getAggregators(callback) {
		getRequest("Aggregators", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «PaymentMethods», отдающий список способов оплаты текущего предприятия
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, PaymentMethods : [object] }} data ответ от сервера
	 */
	getPaymentMethods(callback) {
		getRequest("PaymentMethods", {}, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С ПРИЧИНАМИ СПИСАНИЯ ----- */
export const CancelReasonApi = {
	/**
	 * API метод «CancelReasons», отдающий список причин списания
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, CancelReasons : [object] }} data ответ от сервера
	 */
	getList(callback) {
		getRequest("CancelReasons", {}, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С ПРОДУКТАМИ ----- */
export const ProductApi = {
	/**
	 * API метод «Products», отдающий список продуктов с учетом фильтра
	 * @param {{ Active : boolean, Type? : number }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ от сервера
	 */
	getList(params = { Active : true }, callback) {
		getRequest("Products", params, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ СО СКЛАДАМИ ----- */
export const StockApi = {
	/**
	 * API метод «Stock», отдающий склад по ID
	 * @param {number} id ID склада
	 * @param {boolean} [onlyProducts=false] флаг получения только продуктов склада
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ от сервера
	 */
	getById(id, onlyProducts = false, callback) {
		getRequest("Stock", { Id : id, OnlyProducts : onlyProducts }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «StockActual», отдающий текущие остатки продуктов склада
	 * @param {number} id ID склада
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ от сервера
	 */
	getActual(id, callback) {
		getRequest("StockActual", { Id : id }, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ С ЗАЯВКАМИ НА СПИСАНИЕ ----- */
export const WriteOffRequestApi = {
	/**
	 * API метод «WriteOffRequests», отдающий список заявок на списание
	 * @param {{ StockId : number, Status : number }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Requests : [object] }} data ответ от сервера
	 */
	getList(params, callback) {
		getRequest("WriteOffRequests", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «CreateWriteOffRequest», создающий заявку на списание
	 * @param {object} params данные заявки
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	create(params, callback) {
		getRequest("CreateWriteOffRequest", params, (data) => { if (callback) callback(data); });
	}
};

//** ----- МЕТОДЫ РАБОТЫ СО СПИСАНИЯМИ ----- */
export const WriteOffApi = {
	/**
	 * API метод «WriteOffs», отдающий список списаний с учетом фильтра
	 * @param {object} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, WriteOffs : [object], HasMore : boolean }} data ответ от сервера
	 */
	getList(params, callback) {
		getRequest("WriteOffs", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «WriteOff», отдающий списание по ID
	 * @param {number} id ID списания
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	getById(id, callback) {
		getRequest("WriteOff", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «WriteOff», отдающий текущее списание по складу
	 * @param {number} stockId ID склада
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	getByStockId(stockId, callback) {
		getRequest("WriteOff", { StockId : stockId }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «EditWriteOff», создающий или сохраняющий списание
	 * @param {object} params данные списания
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
	 */
	save(params, callback) {
		getRequest("EditWriteOff", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «AcceptWriteOff», подтверждающий списание
	 * @param {number} id ID списания
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	accept(id, callback) {
		getRequest("AcceptWriteOff", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SendWriteOff», отправляющий списание
	 * @param {number} id ID списания
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	send(id, callback) {
		getRequest("SendWriteOff", { Id : id }, (data) => { if (callback) callback(data); });
	}
};


//** ----- МЕТОДЫ РАБОТЫ С ФИЛИАЛАМИ ----- */
//** ----- НОВЫЕ МЕТОДЫ РАБОТЫ С ФИЛИАЛАМИ ----- */
export const FilialApi = {
	/**
	 * API метод «Filials», отдающий список доступных филиалов пользователя
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Filials : [object] }} data ответ от сервера
	 */
	getList(callback) {
		getRequest("Filials", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Filial», отдающий филиал по ID
	 * @param {number} id ID филиала
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	getById(id, callback) {
		getRequest("Filial", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «FilialSave», сохраняющий филиал
	 * @param {object} params параметры формы филиала
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
	 */
	save(params, callback) {
		getRequest("FilialSave", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «FilialRemove», удаляющий филиал по ID
	 * @param {number} id ID филиала
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	remove(id, callback) {
		getRequest("FilialRemove", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «FiscalCassaList», отдающий список непривязанных фискальных касс
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, FiscalCassaList : [object] }} data ответ от сервера
	 */
	getFiscalCassaList(callback) {
		getRequest("FiscalCassaList", {}, (data) => { if (callback) callback(data); });
	}
};



//** ----- МЕТОДЫ РАБОТЫ С АККАУНТОМ ПОЛЬЗОВАТЕЛЯ ----- */
/** */
export function getAccount(params = {}, callback) {
	getRequest("Account", params, (data) => { if (callback) callback(data); }); 
}

//** ----- МЕТОДЫ РАБОТЫ С ЧЕК ЛИСТАМИ ----- */
/** API метод «Checklists», отдающий список чек листов 
 * @param {boolean} template флаг запроса шаблонов/чек листов предприятия
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string, Checklists : [object] }} data ответ от сервера
 */
export function getCheckLists(template=false, callback) {
	getRequest("Checklists", { Templates : template }, (data) => { if (callback) callback(data); });
}
/** API метод «Checklist», отдающий чек лист */
export function getCheckListbyId(id, callback) {
	getRequest("CheckList", { Id : id }, (data) => { if (callback) callback(data); });
}
/** API метод «ChecklistRemove», удаляющий чек лист 
 * @param {number} id ID удаляемой задачи
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
 */
export function removeCheckListbyId(id, callback) {
	getRequest("ChecklistRemove", { Id : id }, (data) => { if (callback) callback(data); });
}
/** API метод «ChecklistSave», для сохранения чек листа 
 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
 */
export function saveCheckList(params, callback) {
	getJSONRequest("ChecklistSave", params, (data) => { if (callback) callback(data); });
}

/** API метод «TasksList», отдающий список задач на сегодня 
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string, Tasks : [object] }} data ответ от сервера
 */
export function getTasks(callback) {
	getRequest("TasksList", {}, (data) => { if (callback) callback(data); });
}

//** ----- МЕТОДЫ РАБОТЫ С КОНТЕНТ-СТРАНИЦАМИ ----- */
export const ContentApi = {
	/** API метод «GetContent», отдающий статьи страницы активного рабочего стола */
	getList(id, callback) {
		getRequest("GetContent", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/** API метод «ChangeStateContent», изменяющий видимость статьи 
	 * @param {number} id ID статьи
	 * @param {boolean} active флаг изменяемого состояния
	 * @param {function} callback функция обработки результата
	 * @returns {{ Id : number, Active : boolean }}
	 */
	toggle(id, active, callback) {
		getRequest("ChangeStateContent", { Id : id, Active : active }, (data) => { if (callback) callback(data); });
	},

	/** API метод «RemoveContent», удаляющий статью 
	 * @param {number} id ID статьи
	 * @param {function} callback функция обработки результата
	 * @returns {{ Id : number }}
	 */
	remove(id, callback) {
		getRequest("RemoveContent", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/** API метод «SaveContent», сохраняющий статью */
	save(params, callback) {
		getRequest("SaveContent", params, (data) => { if (callback) callback(data); });
	},

	/** API метод «ChangeOrderContent», сохраняющий название раздела или порядок его статей */
	saveGroup(params, callback) {
		getRequest("ChangeOrderContent", params, (data) => { if (callback) callback(data); });
	},

	/** API метод «GroupOrder», сохраняющий порядок разделов */
	saveGroupOrder(params, callback) {
		getRequest("GroupOrder", params, (data) => { if (callback) callback(data); });
	}
};

/** API метод «DoneTask», выполняющий задачу 
 * @param {{ Id : number, Comment : string, Photos : [file]}} params параметры запроса
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
 */
export function doneTaskbyId(params, callback) {
	getUpload("DoneTask", params, (data) => { if (callback) callback(data); });
}

/** API метод «ChecklistReport, получающий отчет по чек листам в виде групп 
 * @param {object} params параметры запроса
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string, TaskReportGroups : [object], HasMore : boolean }} data ответ от сервера
 */
export function getTasksReportGroup(params, callback) {
	getRequest("ChecklistReport", params, (data) => { if (callback) callback(data); });
}

/** API метод «ChecklistStats», получающий статистику выполнения задач
 * @param {object} params параметры запроса
 * @param {funсtion} callback функция обработки результата
 */
export function getTasksReportStat(params, callback) {
	getRequest("ChecklistStats", params, (data) => { if (callback) callback(data); });
}

/** API метод «ChecklistReport, получающий отчет по конкретной группе чек листа
 * @param {string} params ключ группы
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string, TaskReportElements : [object] }} data ответ от сервера
 */
export function getTasksReportGroupById(group, callback) {
	getRequest("GetGroupTasks", { GroupName : group }, (data) => { if (callback) callback(data); });
}

/** API метод «ControlTasksList», получающий список задач, по которым нужно провести ревью
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string, Tasks : [object] }} data ответ от сервера
 */
export function getTasksOnControl(callback) {
	getJSONRequest("ControlTasksList", { OnControl : true }, (data) => { if (callback) callback(data); });
}

/** API метод «ReviewTask», принимающая/возвращающая задачу
 * @param {{ Id : number, Comment : string, Approve : boolean, Photos : [file]}} params ключ группы
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
 */
export function reviewTaskById(params, callback) {
	getUpload("ReviewTask", params, (data) => { if (callback) callback(data); });
}


//** ----- API-МЕТОДЫ САНИТАРНЫХ ЖУРНАЛОВ ----- */
export const SanJournalApi = {
	/**
	 * API метод «SanitaryJournals» получения списка СЖ
	 * @param {boolean} isTemplate флаг шаблона, если false – отдаются журналы предприятия, если true – шаблоны СЖ
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, List : [object] }} data ответ от сервера
	 */
	getList(isTemplate = false, callback) {
		getJSONRequest("SanitaryJournals", { Templates : isTemplate }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SanitaryJournal» получения СЖ по ID
	 * @param {number} id ID СЖ
	 * @param {function} callback функция обработки результата
	 */
	getById(id, callback) {
		getJSONRequest("SanitaryJournal", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SaveSanitaryJournal» для сохранения СЖ
	 * @param {object} params данные по СЖ
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Id : number }} data ответ от сервера
	 */
	save(params, callback) {
		getJSONRequest("SaveSanitaryJournal", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «DeleteSanitaryJournal», удаляющий СЖ
	 * @param {number} id ID СЖ
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	remove(id, callback) {
		getJSONRequest("DeleteSanitaryJournal", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «GetSanJournals» получения журналов и строк для заполнения
	 * @param {function} callback функция обработки результата
	 */
	getFilling(callback) {
		getJSONRequest("GetSanJournals", {}, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SaveSJData» сохранения заполненных строк журналов
	 * @param {object} params параметры запроса
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} data ответ от сервера
	 */
	saveFilling(params, callback) {
		getJSONRequest("SaveSJData", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «GetSanJournalReportStats» получения списка заполненных СЖ
	 * @param {{ DateFrom : string, DateTo : string, FilialId : number, JournalType : number }} params параметры запроса
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Journals : [object] }} data ответ от сервера
	 */
	getReportList(params, callback) {
		getJSONRequest("GetSanJournalReportStats", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «GetSanJournalReportSingle» получения заполненного СЖ по ID
	 * @param {{ DateFrom : string, DateTo : string, JournalId : number }} params параметры запроса
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Journals : object }} data ответ от сервера
	 */
	getReportById(params, callback) {
		getJSONRequest("GetSanJournalReportSingle", params, (data) => { if (callback) callback(data); });
	}
};


//** ----- РАБОТА С ПОСТАВЩИКАМИ ----- */
export const ProviderApi = {
	/**
	 * API метод «Providers», отдающий список поставщиков
	 * @param {boolean} active фильтр активности поставщиков
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success: boolean, ErrorCode: string, Providers: [object] }} data ответ сервера
	 */
	getList(active = true, callback) {
		getRequest("Providers", { Active : active }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Provider», отдающий данные поставщика по ID
	 * @param {number} id ID поставщика
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success: boolean, ErrorCode: string, Id: number, Name: string, Active: boolean, CityId: number, Description: string, INN: string, KPP: string, BIK: string, Bank: string, CorrAccount: string, CheckAccount: string, Address: string, FactAddress: string, DefermentPayment: number, MinAmount: number, Contacts: [object], WorkHours: [object], Products: [object] }} data ответ сервера
	 */
	getById(id, callback) {
		getRequest("Provider", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «ProviderHistory», отдающий историю закупок у поставщика
	 * @param {number} id ID поставщика
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success: boolean, ErrorCode: string, Orders: [{ Type: number, Id: number, ListId: string, Date: string, Amount: number, Status: number }] }} data ответ сервера
	 */
	getInvoiceHistory(id, callback) {
		getRequest("ProviderHistory", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SaveProvider», сохраняющий поставщика
	 * @param {object} params данные поставщика, контактов, графика работы и товаров
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success: boolean, ErrorCode: string, Id: number }} data ответ сервера
	 */
	save(params, callback) {
		getRequest("SaveProvider", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «RemoveProvider», удаляющий поставщика по ID
	 * @param {number} id ID поставщика
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success: boolean, ErrorCode: string }} data ответ сервера
	 */
	remove(id, callback) {
		getRequest("RemoveProvider", { Id : id }, (data) => { if (callback) callback(data); });
	}
};

/** ----- Работа с цехами ----- */
export function getWorkShopsList(callback) {
	getRequest("WorkShops", {}, (data) => { if (callback) callback(data); });
}
export function saveWorkShop(params, callback) {
	getRequest("SaveWorkShop", params, (data) => { if (callback) callback(data); });
}
export function removeWorkShopbyId(id, callback) {
	getRequest("RemoveWorkShop", {Id : id}, (data) => { if (callback) callback(data); });
}






/**
 * API метод Sections для получения списка разделов товаров
 * @param {funсtion} callback функция обработки результата
 * @returns {{ Success: boolean, ErrorCode : string, Sections : [object]}}
 */
export function getSectionsList (callback) {
	getRequest("Sections", {}, (data) => { if (callback) callback(data); });
}

//** ----- Работа с заявками на закупку ----- */
export const RequestApi = {
	getList(callback) {
		getRequest("Requests", {}, (data) => { if (callback) callback(data); });
	},
	remove (id, callback) {
		getRequest("RemoveRequest", { Id: id }, (data) => { if (callback) callback(data) });
	},
	create (params, callback) {
		getRequest("NewRequest", params, (data) => { if (callback) callback(data) });
	},
	process (params, callback) {
		getRequest("ProcessRequest", params, (data) => { if (callback) callback(data) });
	}
}

//** ----- Работа со списками покупок ----- */
export const ShopListApi = {
	/**
	 * API метод «ShopLists» получения списков покупок с учетом фильтра
	 * @param {{ Status? : number }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, ShopLists : [object] }} ответ метода
	 */
	getList(params = {}, callback) {
		getRequest("ShopLists", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «ShopList» получения списка покупок по ID
	 * @param {string} id ID списка покупок
	 * @param {function} callback функция обработки результата
	 * @returns {object} ответ метода
	 */
	getById(id, callback) {
		getRequest("ShopList", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SaveShopList» сохранения списка покупок
	 * @param {object} params данные списка покупок
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} ответ метода
	 */
	save(params, callback) {
		getRequest("SaveShopList", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SendShopList» отправки списка покупок поставщику
	 * @param {string} id ID списка покупок
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} ответ метода
	 */
	send(id, callback) {
		getRequest("SendShopList", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «RemoveShopList» удаления списка покупок
	 * @param {string} id ID списка покупок
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string }} ответ метода
	 */
	remove(id, callback) {
		getRequest("RemoveShopList", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «ShopListPDF» скачивания списка покупок в PDF
	 * @param {string} id ID списка покупок
	 */
	download(id) {
		getFile("ShopListPDF", { Id : id }, "File.pdf");
	}
};

//** ----- Работа с накладными ----- */
export const InvoiceApi = {
	/**
	 * API метод «InvoicesCalendar» получения календаря платежей
	 * @param {string} date месяц в формате YYYY-MM
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success: boolean, ErrorCode: string, Dates: [object] }} ответ метода
	 */
	getCalendar(date, callback) {
		getRequest("InvoicesCalendar", { Date: date }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Invoices» получения списка накладных
	 * @param {{StockId : number, ProductId: number, Date: string, Status: number, Limit: number, Offset: number}} params фильтр накладных
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success: boolean, ErrorCode: string, Invoices : [object] }} ответ метода
	 */
	getList(params, callback) {
		getRequest("Invoices", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «Invoice» получения накладной по ID
	 * @param {number} id ID накладной
	 * @param {function} callback функция обработки результата
	 * @returns {object} ответ метода
	 */
	getById(id, callback) {
		getRequest("Invoice", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «SaveInvoice» сохранения накладной
	 * @param {object} params параметры накладной
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode: string, Id : number }} ответ метода
	 */
	save(params, callback) {
		getRequest("SaveInvoice", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «AcceptInvoice» подтверждения накладной
	 * @param {number} id ID накладной
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode: string }} ответ метода
	 */
	accept(id, callback) {
		getRequest("AcceptInvoice", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «RemoveInvoice» удаления накладной
	 * @param {number} id ID накладной
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode: string, Id : number }} ответ метода
	 */
	remove(id, callback) {
		getRequest("RemoveInvoice", { Id : id }, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «PaymentInvoice» оплаты накладной
	 * @param {number} id ID накладной
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode: string, Id : number }} ответ метода
	 */
	pay(id, callback) {
		getRequest("PaymentInvoice", { Id : id }, (data) => { if (callback) callback(data); });
	}
};


export const StockReportApi = {
	/**
	 * API метод «StatCost» получения отчета по себестоимости в виде отчета или файла
	 * @param {{ DateFrom : string, DateTo: string, Type: number, SectionIds?: [number], CityId : number, ShowComposition : boolean, OutForm : string }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ сервера
	 */
	getCost(params, callback) {
		if (params.OutForm === "json")
			getRequest("StatCost", params, (data) => { if (callback) callback(data); });
		else if (params.OutForm === "file")
			getFile("StatCost", params, "Cost_Report.csv");
	},

	/**
	 * API метод «StatRevisions» получения отчета по ревизиям в виде отчета или файла
	 * @param {{ DateFrom : string, DateTo : string, Type : number, SectionIds? : [number], ProductIds? : [number], FilialIds? : [number], StockIds? : [number], OutForm : string }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ сервера
	 */
	getRevision(params, callback) {
		if (params.OutForm === "json")
			getRequest("StatRevisions", params, (data) => { if (callback) callback(data); });
		else if (params.OutForm === "file")
			getFile("StatRevisions", params, "File.csv");
	},

	/**
	 * API метод «GetWorkshopLoadReport» получения отчета по загрузке цехов
	 * @param {{ DateFrom : string, DateTo : string, FilialIds : [number], SectionIds : [number], WorkShopIds : [number] }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Items : [object], Total : { Count : number, Amount : number } }} data ответ сервера
	 */
	getWorkShop(params, callback) {
		getJSONRequest("GetWorkshopLoadReport", params, (data) => { if (callback) callback(data); });
	},

	/**
	 * API метод «StatOrders» получения отчета по продажам в виде отчета или файла
	 * @param {{ DateFrom : string, DateTo: string, ProductIds?: [number], SectionIds?: [number], FilialIds?: [number], StockIds?: [number], OnlySales : boolean, OutForm : string }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ сервера
	 */
	getSales(params, callback) {
		if (params.OutForm === "json")
			getRequest("StatOrders", params, (data) => { if (callback) callback(data); }); 
		else if (params.OutForm === "file")
			getFile("StatOrders", params, "Sales_Report.csv"); 
	},

	/**
	 * API метод «StatPurchase» получения отчета по накладным в виде отчета или файла
	 * @param {{ DateFrom : string, DateTo : string, Type : number, SectionIds? : [number], ProductIds? : [number], FilialIds? : [number], StockIds? : [number], ProviderIds? : [number], Summary : boolean, OutForm : string }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ сервера
	 */
	getPurchase(params, callback) {
		if (params.OutForm === "json")
			getRequest("StatPurchase", params, (data) => { if (callback) callback(data); });
		else if (params.OutForm === "file")
			getFile("StatPurchase", params, "File.csv");
	},

	/**
	 * API метод «StatRawMovement» получения отчета по движению сырья в виде отчета или файла
	 * @param {{ DateFrom : string, DateTo : string, Type : number, SectionIds? : [number], ProductIds? : [number], FilialIds? : [number], StockIds? : [number], OutForm : string }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ сервера
	 */
	getRawMovement(params, callback) {
		if (params.OutForm === "json")
			getRequest("StatRawMovement", params, (data) => { if (callback) callback(data); });
		else if (params.OutForm === "file")
			getFile("StatRawMovement", params, "File.csv");
	},

	/**
	 * API метод «WriteOffStat» получения отчета по списаниям
	 * @param {{ DateFrom : string, DateTo: string, Type: number, SectionIds?: [number], ProductIds?: [number], FilialIds?: [number], StockIds?: [number], CancelReasonIds?: [number], Summary : boolean, OutForm : string }} params параметры фильтра
	 * @param {function} callback функция обработки результата
	 * @returns {{ Success : boolean, ErrorCode : string, Products : [object] }} data ответ сервера
	 */
	getWriteOff(params, callback) {
		getRequest("WriteOffReportStat", params, (data) => { if (callback) callback(data); });
	}
};
